// Tracking helpers: Google Ads, Meta Pixel, Microsoft Clarity.
// GA4 is not loaded here: it runs inside Google Tag Manager (GTM-PB94GLKW,
// see index.html, together with the Consent Mode v2 defaults).
// All loaders are idempotent and only fire after the user grants consent
// via the CookieBanner. `trackPageview` / `trackOutboundClick` are safe
// no-ops when the corresponding tracker hasn't been loaded.

import { hasPerfFlag } from "./perf-flags";

export const ADS_ID = "AW-18385836607"; // Google Ads (conversiones)
export const GA4_ID = "G-CN8H9ZLX3X"; // GA4, loaded by GTM
export const PIXEL_ID = "1285768940105740";
export const CLARITY_ID = "xsda69wd56";

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: any;
    _fbq?: any;
    clarity?: any;
    __ulture_gtagjs_loaded?: boolean;
    __ulture_ads_loaded?: boolean;
    __ulture_clarity_loaded?: boolean;
    __ulture_pixel_loaded?: boolean;
    /** index.html: runs fn once GTM has been injected (on the phone home it waits ~2.5 s or for the first tap). */
    __ulLater?: (fn: () => void) => void;
  }
}

// [ULTURE] Third-party script downloads wait for GTM (see index.html): on the phone home they would land
// right while the page is being built. The stubs (gtag, clarity, fbq) still queue every call meanwhile.
function later(fn: () => void) {
  if (window.__ulLater) window.__ulLater(fn);
  else fn();
}

function ensureGtag() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
}

function injectScript(src: string, attrs: Record<string, string> = {}) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

// gtag.js para Google Ads (GA4 va por GTM).
function ensureGtagJs() {
  if (window.__ulture_gtagjs_loaded) return;
  window.__ulture_gtagjs_loaded = true;
  later(() => injectScript(`https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`));
  window.gtag!("js", new Date());
}

// Clarity — analytics bucket. The consent update unlocks GA4 inside GTM.
export function loadAnalytics() {
  if (typeof window === "undefined") return;
  if (hasPerfFlag("notrack")) return; // diagnóstico: ?notrack=1
  ensureGtag();
  window.gtag!("consent", "update", { analytics_storage: "granted" });

  // Clarity graba la sesión (engancha scroll + mutaciones del DOM en continuo):
  // es el principal sospechoso del jank en webviews. ?noclarity=1 lo deja fuera
  // sin tocar GA4, para poder aislarlo.
  if (!window.__ulture_clarity_loaded && !hasPerfFlag("noclarity")) {
    window.__ulture_clarity_loaded = true;
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
      later(() => {
        const t = l.createElement(r) as HTMLScriptElement;
        t.async = true;
        t.src = "https://www.clarity.ms/tag/" + i;
        const y = l.getElementsByTagName(r)[0];
        y.parentNode!.insertBefore(t, y);
      });
    })(window, document, "clarity", "script", CLARITY_ID);
  }
}

// Google Ads + Meta Pixel — marketing bucket
export function loadMarketing() {
  if (typeof window === "undefined") return;
  if (hasPerfFlag("notrack")) return; // diagnóstico: ?notrack=1
  ensureGtag();
  window.gtag!("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });

  if (!window.__ulture_ads_loaded) {
    window.__ulture_ads_loaded = true;
    ensureGtagJs();
    window.gtag!("config", ADS_ID);
  }

  if (window.__ulture_pixel_loaded) return;
  window.__ulture_pixel_loaded = true;

  /* eslint-disable */
  // Standard Meta Pixel bootstrap.
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    later(() => {
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      const s = b.getElementsByTagName(e)[0];
      s.parentNode!.insertBefore(t, s);
    });
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq("init", PIXEL_ID);
  trackPageview(window.location.pathname + window.location.search);
}

// Pixel page the last PageView was sent for: loading the pixel, the consent event and the
// route change all report the page they're on, and each page counts once.
let lastPixelPage: string | null = null;

// Fired on every SPA route change. Silent no-op when trackers aren't loaded.
export function trackPageview(path: string) {
  if (typeof window === "undefined") return;
  // GA4 (GTM) counts SPA route changes itself via enhanced measurement.
  if (window.__ulture_pixel_loaded && window.fbq && path !== lastPixelPage) {
    lastPixelPage = path;
    window.fbq("track", "PageView");
  }
}

/**
 * A GA4 event, straight to the Google tag that GTM loads: with `send_to` it needs no GTM
 * tag or trigger. Consent Mode applies as for page views (cookieless ping until the
 * visitor accepts analytics). Never pass personal data.
 */
export function ga4Event(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined" || hasPerfFlag("notrack")) return;
  ensureGtag();
  window.gtag!("event", name, { ...params, send_to: GA4_ID });
}

// Outbound click tracking for CTAs on /apps.
export function trackOutboundClick(label: string, url: string) {
  if (typeof window === "undefined") return;
  ga4Event("select_app", { app_name: label, link_url: url });
  if (window.__ulture_pixel_loaded && window.fbq) {
    window.fbq("trackCustom", "ClicApp", { app_name: label });
  }
  if (window.clarity) {
    try {
      window.clarity("set", "app_clicked", label);
    } catch {
      /* noop */
    }
  }
}

// Read the consent stored by CookieBanner.tsx.
const COOKIE_CONSENT_KEY = "ulture_cookie_consent";
export type StoredConsent = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
};

export function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
}

export const CONSENT_EVENT = "ulture:consent-changed";